import { useState } from "react";
import { ChevronLeft, ChevronRight, Check, X, Upload, Mic, Heart } from "lucide-react";

/** Studio Wizard — PREVIEW (steps 1-3 only).
 *
 *  Shows the proposed guided-mode UX so the creator can decide whether
 *  the full 9-step wizard is worth building. State here is local to the
 *  wizard — accepting a step doesn't yet wire into the real upload /
 *  render pipeline; that's deliberate (~10 minute build vs ~45 minute
 *  full integration). Once approved, each step's `body` becomes a
 *  thin wrapper around the existing studio inputs in App.js.
 */

const TOTAL_STEPS = 9;
const PREVIEW_STEPS = [1, 2, 3];

const CATEGORIES = [
  { id: "general",       label: "General",        emoji: "📖" },
  { id: "animal_rescue", label: "Animal Rescue",  emoji: "🐾" },
  { id: "charity",       label: "Charity",        emoji: "❤️" },
  { id: "cancer",        label: "Cancer",         emoji: "🎗️" },
  { id: "dementia",      label: "Dementia",       emoji: "💜" },
  { id: "mental_health", label: "Mental health",  emoji: "💚" },
  { id: "child_welfare", label: "Child welfare",  emoji: "🤍" },
  { id: "environment",   label: "Environment",    emoji: "🌍" },
];

// Sensitivity guidance shown in the "Why this matters" panel for selected
// categories. Helps creators match accent / tone / language at the right
// moment in the flow.
const SENSITIVITY_NOTE = {
  cancer: "For cancer stories, prefer Royal purple accent (or pink for breast cancer) and an Informational tone — Urgent red can feel exploitative. Always get written consent from the subject or family.",
  dementia: "Living-with-dementia stories work best in Ocean blue with a calm, present-tense voice. Anonymise unless explicit consent is given.",
  mental_health: "Use Forest green and avoid sensational language. Always link to Mind / Samaritans in the donation field, even if there's no fundraising goal.",
  child_welfare: "Anonymise the child. Blur faces in the video. Use first names or pseudonyms only. Get guardian consent in writing.",
};

export default function StudioWizard({ onClose }) {
  const [step, setStep] = useState(1);

  // Local state — would be lifted into App.js when wired.
  const [videoName, setVideoName] = useState("");
  const [narrationMode, setNarrationMode] = useState("extract"); // "extract" | "upload"
  const [audioName, setAudioName] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("general");
  const [summary, setSummary] = useState("");
  const [showOptional, setShowOptional] = useState(false);
  const [location, setLocation] = useState("");
  const [donationLink, setDonationLink] = useState("");

  const canProceed = (() => {
    if (step === 1) return Boolean(videoName);
    if (step === 2) return narrationMode === "extract" || Boolean(audioName);
    if (step === 3) return Boolean(title.trim());
    return true;
  })();

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const stepLabel = {
    1: "Upload your video",
    2: "Get the narration in",
    3: "Story details",
    4: "Tail clips",
    5: "Branded end-card",
    6: "Generate",
    7: "Article",
    8: "Share card",
    9: "Publish",
  }[step];

  const isPreviewStep = PREVIEW_STEPS.includes(step);

  const fieldCls = "w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[color:var(--accent)] transition-colors";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-[color:var(--bg)] overflow-auto"
      data-testid="studio-wizard"
    >
      <div className="panel max-w-3xl w-full p-6 md:p-10">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5">
              Guided mode · preview
            </div>
            <div className="font-display text-3xl leading-tight">{stepLabel}</div>
            <div className="text-xs text-[color:var(--muted)] mt-1">
              Step {step} of {TOTAL_STEPS}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[color:var(--muted)] hover:text-white transition-colors"
            aria-label="Exit wizard"
            data-testid="studio-wizard-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5 mb-8">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${i + 1 < step ? "bg-[color:var(--accent)]" : i + 1 === step ? "bg-[color:var(--accent)]/70" : "bg-white/10"}`}
            />
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="space-y-5" data-testid="wizard-step-1">
            <p className="text-sm text-[color:var(--muted)] leading-relaxed">
              Drop in the visuals first — a short MP4 between 30 seconds and 5 minutes works best. Your phone's camera is more than enough quality.
            </p>
            <label className="block border-2 border-dashed border-white/10 hover:border-[color:var(--accent)]/50 rounded-2xl p-10 text-center cursor-pointer transition-colors">
              <Upload className="w-8 h-8 mx-auto text-[color:var(--muted)] mb-3" />
              <div className="font-display text-base">Click to upload an MP4</div>
              <div className="text-xs text-[color:var(--muted)] mt-1">Or drag and drop · up to 200 MB</div>
              <input
                type="file"
                accept="video/mp4"
                className="hidden"
                onChange={(e) => setVideoName(e.target.files?.[0]?.name || "")}
              />
            </label>
            {videoName && (
              <div className="flex items-center gap-2 text-sm p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 text-emerald-300">
                <Check className="w-4 h-4" />
                <span className="truncate">{videoName}</span>
              </div>
            )}
            <WhyPanel>
              The video carries the visuals — facial expressions, the place, the action. Narration goes on top in step 2. Phone footage is fine; cinematic quality is not the goal.
            </WhyPanel>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="space-y-5" data-testid="wizard-step-2">
            <p className="text-sm text-[color:var(--muted)] leading-relaxed">
              Storylo needs spoken narration so it can transcribe, translate, and re-voice the story in 14 languages.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => setNarrationMode("extract")}
                className={`text-left p-4 rounded-xl border transition-colors ${narrationMode === "extract" ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5" : "border-white/10 hover:border-white/30"}`}
              >
                <Mic className="w-5 h-5 text-[color:var(--accent)] mb-2" />
                <div className="font-display text-base">Extract from video</div>
                <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed">
                  Narration is already baked into the video (the common case for animations).
                </div>
              </button>
              <button
                onClick={() => setNarrationMode("upload")}
                className={`text-left p-4 rounded-xl border transition-colors ${narrationMode === "upload" ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5" : "border-white/10 hover:border-white/30"}`}
              >
                <Upload className="w-5 h-5 text-[color:var(--accent)] mb-2" />
                <div className="font-display text-base">Upload separately</div>
                <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed">
                  WAV or MP3 the same length as your video.
                </div>
              </button>
            </div>
            {narrationMode === "upload" && (
              <label className="block border-2 border-dashed border-white/10 hover:border-[color:var(--accent)]/50 rounded-xl p-6 text-center cursor-pointer transition-colors">
                <Upload className="w-6 h-6 mx-auto text-[color:var(--muted)] mb-2" />
                <div className="text-sm">Click to upload narration audio</div>
                <input
                  type="file"
                  accept="audio/wav,audio/mpeg,audio/mp3"
                  className="hidden"
                  onChange={(e) => setAudioName(e.target.files?.[0]?.name || "")}
                />
              </label>
            )}
            {audioName && (
              <div className="flex items-center gap-2 text-sm p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 text-emerald-300">
                <Check className="w-4 h-4" />
                <span className="truncate">{audioName}</span>
              </div>
            )}
            <WhyPanel>
              For trauma-sensitive stories, recording in a quiet room with the phone close to your mouth (10-15 cm) is the single biggest quality improvement. Aim for 130-150 words per minute.
            </WhyPanel>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="space-y-5" data-testid="wizard-step-3">
            <p className="text-sm text-[color:var(--muted)] leading-relaxed">
              These details power the homepage card, the social-share preview, and the auto-generated thumbnail.
            </p>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Title <span className="text-red-300 normal-case">*</span></label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="e.g. Atila's story in Tarifa"
                className={fieldCls}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Category <span className="text-red-300 normal-case">*</span></label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    className={`text-left p-3 rounded-lg border text-xs transition-colors ${category === c.id ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5" : "border-white/10 hover:border-white/30"}`}
                  >
                    <div className="text-base mb-1">{c.emoji}</div>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Impact summary <span className="normal-case tracking-normal">(1-2 sentences, optional)</span></label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                maxLength={200}
                rows={2}
                placeholder="Atila was found tied to a fence in southern Spain..."
                className={`${fieldCls} resize-none`}
              />
            </div>
            <button
              onClick={() => setShowOptional((s) => !s)}
              className="text-xs text-[color:var(--muted)] hover:text-[color:var(--accent)] underline-offset-2 hover:underline"
            >
              {showOptional ? "Hide" : "Show"} optional fields (location, donation link)
            </button>
            {showOptional && (
              <div className="space-y-3 pt-2 border-l-2 border-white/10 pl-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Location</label>
                  <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Tarifa, Spain" className={fieldCls} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Donation link</label>
                  <input value={donationLink} onChange={(e) => setDonationLink(e.target.value)} placeholder="https://gofundme.com/..." className={fieldCls} />
                  <div className="text-[10px] text-[color:var(--muted)] mt-1">A donation button will appear on the public story page and a "How you can help" panel under the article.</div>
                </div>
              </div>
            )}
            {SENSITIVITY_NOTE[category] && (
              <div className="p-4 rounded-xl border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/5" data-testid="wizard-sensitivity-note">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] inline-flex items-center gap-1.5 mb-2">
                  <Heart className="w-3.5 h-3.5" /> Why this matters
                </div>
                <div className="text-xs leading-relaxed text-[color:var(--muted)]">
                  {SENSITIVITY_NOTE[category]}
                </div>
              </div>
            )}
          </div>
        )}

        {!isPreviewStep && (
          <div className="py-16 text-center border-2 border-dashed border-white/10 rounded-2xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2">Preview ends here</div>
            <div className="font-display text-2xl">Step {step} · {stepLabel}</div>
            <div className="text-sm text-[color:var(--muted)] mt-3 max-w-md mx-auto leading-relaxed">
              The remaining 6 steps would wire into the same flows you already use in the regular studio — render progress, tail clips, share-card editor, AI polish, publish toggle. Approve the first 3 and I'll build them out.
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
          <button
            onClick={back}
            disabled={step === 1}
            className="text-sm px-4 py-2 rounded-md border border-white/10 hover:border-white/30 transition-colors inline-flex items-center gap-1.5 disabled:opacity-30"
            data-testid="studio-wizard-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
            Preview · changes here are not saved
          </div>
          <button
            onClick={next}
            disabled={!canProceed || step === TOTAL_STEPS}
            className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid="studio-wizard-next"
          >
            {step < 3 ? "Next" : "See remaining steps"} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function WhyPanel({ children }) {
  return (
    <div className="p-4 rounded-xl border border-white/10 bg-white/[.02]">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2">Why this matters</div>
      <div className="text-xs leading-relaxed text-[color:var(--muted)]">{children}</div>
    </div>
  );
}
